<template>
  <div class="page-container" v-loading="syncLoading" element-loading-text="正在同步行程表数据，请稍候..." element-loading-background="rgba(255, 255, 255, 0.8)">
    <PageHeader title="纷享销客行程表记录">
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-refresh" :loading="syncLoading" @click="handleSync">更新</el-button>
        <el-button type="warning" size="small" icon="el-icon-time" :disabled="!selectedRows.length" @click="handleBatchExpired">批量过期</el-button>
      </template>
    </PageHeader>

    <!-- 搜索面板 -->
    <SearchPanel>
      <el-input v-model="query.afiProjectName" clearable placeholder="行程名称" size="small" style="width: 160px;" />
      <el-input v-model="query.afiOrderNum" clearable placeholder="销售订单号" size="small" style="width: 160px;" />
      <el-select v-model="query.rfiStatusHandler" clearable placeholder="处理状态" size="small" @change="handleSearch">
        <el-option label="未处理" :value="1"></el-option>
        <el-option label="已处理" :value="2"></el-option>
        <el-option label="处理失败" :value="3"></el-option>
      </el-select>
      <el-select v-model="query.rfiStatus" clearable placeholder="行程状态" size="small" @change="handleSearch">
        <el-option label="正常" :value="1"></el-option>
        <el-option label="已过期" :value="2"></el-option>
      </el-select>
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <!-- 列表 -->
    <TablePanel>
      <aj-table
        v-loading="loading"
        :data="list"
        :columns="columns"
        :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize"
        :page-sizes="[10, 20, 30, 50]"
        :total="total"
        row-id-key="rfiId"
        stripe
        use-internal-pagination
        @page-change="handlePageChange"
        @operate-btn-click="handleOperateBtnClick"
        @selection-change="handleSelectionChange"
      />
      <EmptyState v-if="!loading && list.length === 0" title="暂无行程表记录" icon="el-icon-document"></EmptyState>
    </TablePanel>

    <!-- 详情弹窗 -->
    <el-dialog
      v-dialog-drag-resize
      :visible.sync="detailVisible"
      width="80%"
      top="7vh"
      custom-class="detail-dialog"
      :close-on-click-modal="false"
      :show-close="false"
    >
      <template #title>
        <div class="detail-dialog-header">
          <div class="detail-dialog-title-row">
            <span class="detail-dialog-title">行程表记录详情</span>
            <span v-if="detailData" class="detail-status-tag" :class="handlerStatusClass(detailData.rfiStatusHandler)">
              {{ handlerStatusMap[detailData.rfiStatusHandler] || '未知' }}
            </span>
          </div>
          <i class="dialog-close-btn" @click="detailVisible = false" />
        </div>
      </template>
      <div class="detail-content" v-if="detailData">
        <!-- 基本信息 -->
        <div class="detail-section-title">
          <i class="el-icon-info"></i>
          <span>行程信息</span>
        </div>
        <div class="detail-table detail-table--2col">
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">行程名称</div>
            <div class="detail-cell detail-cell--value">{{ detailData.rfiProjectName || '-' }}</div>
            <div class="detail-cell detail-cell--label">课程名称</div>
            <div class="detail-cell detail-cell--value">{{ detailData.rfiCourseName || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">甲方单位</div>
            <div class="detail-cell detail-cell--value">{{ detailData.rfiProjectCustomer || '-' }}</div>
            <div class="detail-cell detail-cell--label">客户简称</div>
            <div class="detail-cell detail-cell--value">{{ detailData.rfiProjectCustomerShort || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">销售订单号</div>
            <div class="detail-cell detail-cell--value" style="grid-column: span 3;">{{ detailData.rfiOrderNum || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">项目分类</div>
            <div class="detail-cell detail-cell--value">{{ detailData.rfiProjectType || '-' }}</div>
            <div class="detail-cell detail-cell--label">课程标记</div>
            <div class="detail-cell detail-cell--value">{{ detailData.rfiCourseSign || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">开始日期</div>
            <div class="detail-cell detail-cell--value">{{ detailData.rfiCourseDateStart || '-' }}</div>
            <div class="detail-cell detail-cell--label">结束日期</div>
            <div class="detail-cell detail-cell--value">{{ detailData.rfiCourseDateEnd || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">需付课酬天数</div>
            <div class="detail-cell detail-cell--value">{{ detailData.rfiPaidDays != null ? detailData.rfiPaidDays : '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">团队成员</div>
            <div class="detail-cell detail-cell--value" style="grid-column: span 3;">{{ detailData.rfiProjectMembers || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">行程状态</div>
            <div class="detail-cell detail-cell--value">
              <span :style="{ color: itineraryStatusColorMap[detailData.rfiStatus] || '#909399', fontWeight: '500' }">{{ itineraryStatusMap[detailData.rfiStatus] || '未知' }}</span>
            </div>
            <div class="detail-cell detail-cell--label">处理状态描述</div>
            <div class="detail-cell detail-cell--value">{{ detailData.rfiStatusHandlerDes || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">创建时间</div>
            <div class="detail-cell detail-cell--value">{{ detailData.createtime || '-' }}</div>
            <div class="detail-cell detail-cell--label">更新时间</div>
            <div class="detail-cell detail-cell--value">{{ detailData.updatetime || '-' }}</div>
          </div>
        </div>

        <!-- 成员列表 -->
        <div class="detail-section-title">
          <i class="el-icon-user"></i>
          <span>行程成员</span>
        </div>
        <el-table :data="detailData.rtFxxkItineraryMembersList || []" border size="small" style="width: 100%;">
          <el-table-column type="index" label="序号" width="80" align="center" />
          <el-table-column prop="rfimMemberName" label="成员姓名" min-width="120" />
          <el-table-column prop="rfiStatus" label="状态" width="100" align="center">
            <template slot-scope="{ row }">
              <span :style="{ color: itineraryStatusColorMap[row.rfiStatus] || '#909399', fontWeight: '500' }">{{ itineraryStatusMap[row.rfiStatus] || '未知' }}</span>
            </template>
          </el-table-column>
        </el-table>
        <EmptyState v-if="!detailData.rtFxxkItineraryMembersList || detailData.rtFxxkItineraryMembersList.length === 0" title="暂无成员数据" icon="el-icon-user" />
      </div>
      <template #footer>
        <el-button size="small" @click="detailVisible = false">关 闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  getRtFxxkItineraryList,
  getRtFxxkItineraryDetails,
  updateRtFxxkItineraryStatusExpired,
  updateRtFxxkItinerarySync,
  normalizeApplyPageResponse
} from '@/api/api'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'

// 处理状态映射
const HANDLER_STATUS_MAP = { 1: '未处理', 2: '已处理', 3: '处理失败' }
const HANDLER_STATUS_COLOR_MAP = { 1: '#f59e0b', 2: '#10b981', 3: '#ef4444' }
// 行程状态映射
const ITINERARY_STATUS_MAP = { 1: '正常', 2: '已过期' }
const ITINERARY_STATUS_COLOR_MAP = { 1: '#4f7cff', 2: '#909399' }

export default {
  name: 'FxxkItineraryListPage',
  components: { AjTable, EmptyState, PageHeader, SearchPanel, TablePanel },
  data () {
    return {
      loading: false,
      syncLoading: false,
      list: [],
      total: 0,
      selectedRows: [],
      // 搜索条件
      query: {
        afiProjectName: '',
        afiOrderNum: '',
        rfiStatusHandler: '',
        rfiStatus: '',
        currentPage: 1,
        pageSize: 10
      },
      // 状态映射
      handlerStatusMap: HANDLER_STATUS_MAP,
      handlerStatusColorMap: HANDLER_STATUS_COLOR_MAP,
      itineraryStatusMap: ITINERARY_STATUS_MAP,
      itineraryStatusColorMap: ITINERARY_STATUS_COLOR_MAP,
      // 详情弹窗
      detailVisible: false,
      detailData: null
    }
  },
  computed: {
    columns () {
      return [
        { type: 'selection', width: 45 },
        { type: 'index', label: '序号', width: 60 },
        { prop: 'rfiProjectName', label: '行程名称', minWidth: 160, showTooltip: true },
        { prop: 'projectCoordination', label: '项目统筹', minWidth: 110, showTooltip: true },
        { prop: 'projectManage', label: '项目经理', minWidth: 110, showTooltip: true },
        { prop: 'projectOwner', label: '负责人', minWidth: 110, showTooltip: true },
        { prop: 'rfiCourseName', label: '课程名称', minWidth: 120, showTooltip: true },
        { prop: 'projectTeacherCharges', label: '讲师负责人', minWidth: 130, showTooltip: true },
        { prop: 'courseTeacherOne', label: '上课老师1', minWidth: 110, showTooltip: true },
        { prop: 'courseTeacherTwo', label: '上课老师2', minWidth: 110, showTooltip: true },
        { prop: 'courseAssistantOne', label: '辅训老师1', minWidth: 110, showTooltip: true },
        { prop: 'courseAssistantTwo', label: '辅训老师2', minWidth: 110, showTooltip: true },
        { prop: 'fiNqAssistant', label: '独立助教', minWidth: 110, showTooltip: true },
        { prop: 'fiNqInternship', label: '实习', minWidth: 100, showTooltip: true },
        { prop: 'fiNqUserOther', label: '其他人员', minWidth: 120, showTooltip: true },
        { prop: 'rfiProjectMembers', label: '团队成员', minWidth: 160, showTooltip: true },
        { prop: 'rfiProjectCustomer', label: '甲方单位', minWidth: 120, showTooltip: true },
        { prop: 'rfiProjectCustomerShort', label: '客户简称', minWidth: 100, showTooltip: true },
        { prop: 'rfiOrderNum', label: '销售订单号', width: 140, showTooltip: true },
        { prop: 'rfiProjectType', label: '项目分类', width: 100 },
        { prop: 'rfiCourseSign', label: '课程标记', width: 100 },
        { prop: 'rfiCourseDateStart', label: '开始日期', width: 110 },
        { prop: 'rfiCourseDateEnd', label: '结束日期', width: 110 },
        { prop: 'rfiPaidDays', label: '需付课酬天数', width: 120, align: 'center' },
        {
          prop: 'rfiStatusHandler',
          label: '处理状态',
          width: 100,
          render: (h, { row }) => {
            const text = HANDLER_STATUS_MAP[row.rfiStatusHandler] || '未知'
            const color = HANDLER_STATUS_COLOR_MAP[row.rfiStatusHandler] || '#909399'
            return h('span', { style: { color, fontWeight: '500' } }, text)
          }
        },
        {
          prop: 'rfiStatus',
          label: '行程状态',
          width: 100,
          render: (h, { row }) => {
            const text = ITINERARY_STATUS_MAP[row.rfiStatus] || '未知'
            const color = ITINERARY_STATUS_COLOR_MAP[row.rfiStatus] || '#909399'
            return h('span', { style: { color, fontWeight: '500' } }, text)
          }
        },
        {
          type: 'operation',
          prop: 'operation',
          label: '操作',
          width: 80,
          fixed: 'right',
          buttons: () => [
            { key: 'view', text: '查看', type: 'text' }
          ]
        }
      ]
    }
  },
  created () {
    this.fetchList()
  },
  methods: {
    // ============ 列表查询 ============
    async fetchList () {
      this.loading = true
      try {
        const params = {}
        if (this.query.afiProjectName) params.afiProjectName = this.query.afiProjectName
        if (this.query.afiOrderNum) params.afiOrderNum = this.query.afiOrderNum
        if (this.query.rfiStatusHandler) params.rfiStatusHandler = this.query.rfiStatusHandler
        if (this.query.rfiStatus) params.rfiStatus = this.query.rfiStatus
        params.currentPage = this.query.currentPage
        params.pageSize = this.query.pageSize
        const res = await getRtFxxkItineraryList(params)
        const pageData = normalizeApplyPageResponse(res)
        this.list = pageData.list
        this.total = pageData.total
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch () {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetQuery () {
      this.query = { afiProjectName: '', afiOrderNum: '', rfiStatusHandler: '', rfiStatus: '', currentPage: 1, pageSize: 10 }
      this.fetchList()
    },
    handlePageChange ({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    },

    // ============ 多选 ============
    handleSelectionChange (rows) {
      this.selectedRows = rows || []
    },

    // ============ 同步更新行程表数据 ============
    async handleSync () {
      try {
        await this.$confirm('确认从外部服务同步最新行程表数据？', '同步确认', { type: 'info' })
      } catch { return }
      this.syncLoading = true
      try {
        await updateRtFxxkItinerarySync()
        this.$message.success('行程表数据已更新')
        this.fetchList()
      } catch (e) {
        this.$message.error('同步行程表数据失败')
      } finally {
        this.syncLoading = false
      }
    },

    // ============ 批量过期 ============
    async handleBatchExpired () {
      if (!this.selectedRows.length) {
        this.$message.warning('请先选择要过期的记录')
        return
      }
      try {
        await this.$confirm(`确认将选中的 ${this.selectedRows.length} 条记录标记为过期？`, '批量过期确认', { type: 'warning' })
        const rfiIds = JSON.stringify(this.selectedRows.map(row => row.rfiId))
        await updateRtFxxkItineraryStatusExpired({ rfiIds })
        this.$message.success('已成功标记为过期')
        this.fetchList()
      } catch (error) {}
    },

    // ============ 查看详情 ============
    async handleView (row) {
      try {
        const res = await getRtFxxkItineraryDetails({ rfiId: row.rfiId })
        this.detailData = res.data || row
        this.detailVisible = true
      } catch (error) {
        this.$message.error('获取行程详情失败')
      }
    },

    // ============ 操作按钮分发 ============
    handleOperateBtnClick ({ btn, row }) {
      if (btn.key === 'view') this.handleView(row)
    },

    // 处理状态样式类
    handlerStatusClass (status) {
      const map = { 1: 'detail-status--warning', 2: 'detail-status--success', 3: 'detail-status--danger' }
      return map[status] || ''
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* ========== 详情弹框 ========== */
::v-deep .detail-dialog {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.12);

  .el-dialog__header {
    padding: 0;
    border-bottom: none;
  }

  .el-dialog__body {
    padding: 0;
    max-height: 70vh;
    overflow-x: hidden;
    overflow-y: auto;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
  }

  .el-dialog__footer {
    padding: 14px 28px;
    border-top: 1px solid #eef1f6;
  }
}

.detail-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 28px 18px;
  border-bottom: 1px solid #eef1f6;
}

.detail-dialog-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.detail-dialog-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}


.detail-content {
  padding: 20px 28px 24px;
}

// 状态标签
.detail-status-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.detail-status--warning {
  background: #fffbeb;
  color: #d97706;
}

.detail-status--success {
  background: #ecfdf5;
  color: #059669;
}

.detail-status--danger {
  background: #fef2f2;
  color: #dc2626;
}

// 分区标题
.detail-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  padding-left: 10px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 16px;
    background: linear-gradient(180deg, #4f7cff, #6366f1);
    border-radius: 2px;
  }

  i {
    font-size: 16px;
    color: #4f7cff;
    margin-left: 4px;
  }

  & + .detail-table,
  & + .el-table {
    margin-bottom: 24px;
  }
}

// 表格布局
.detail-table {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;

  &--2col {
    .detail-row {
      display: grid;
      grid-template-columns: repeat(2, auto 1fr);
    }
  }
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
}

.detail-cell {
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.6;

  &--label {
    background: #f5f7fa;
    color: #606266;
    font-weight: 500;
    white-space: nowrap;
    min-width: 100px;
    width: 100px;
    border-right: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
  }

  &--value {
    background: #fff;
    color: #1e293b;
    flex: 1;
    word-break: break-all;
  }
}

// 操作链接
::v-deep .aj-table-operation-link {
  margin: 0 4px;
  font-size: 12px;
  font-weight: 500;

  &:hover { color: #4f7cff !important; }
}
</style>
